import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import Icon from '../components/common/Icon';
import {
  PRIVACY_POLICY,
  TERMS_EFFECTIVE_DATE,
  TERMS_OF_SERVICE,
  TERMS_VERSION,
} from '../constants/legalContent';

/**
 * Renders the Privacy Policy and the Terms of Service.
 *
 * One component for both because they are the same shape and must look
 * and behave identically - a visitor comparing them should not be able
 * to tell they were built separately, and a change to how legal text is
 * presented should only have to be made once.
 *
 * Public, and deliberately reachable without an account: someone
 * deciding whether to sign up has to be able to read what they would be
 * agreeing to first.
 */
const DOCUMENTS = {
  privacy: PRIVACY_POLICY,
  terms: TERMS_OF_SERVICE,
};

/**
 * Unfilled placeholders are rendered as a visible marker rather than
 * passed through as plain text.
 *
 * Legal text carries entries like "[REGISTERED ADDRESS]" until someone
 * supplies the real value. Rendered plainly, that ships to production
 * looking almost like ordinary prose and nobody notices; rendered like
 * this, it is impossible to miss on the page and in review.
 */
function withPlaceholders(text) {
  const parts = String(text).split(/(\[[^\]]+\])/g);

  return parts.map((part, index) =>
    /^\[[^\]]+\]$/.test(part) ? (
      // eslint-disable-next-line react/no-array-index-key
      <mark key={index} className="legal-placeholder" title="Not filled in yet">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function LegalSection({ section }) {
  return (
    <section
      id={section.id}
      className={`legal-section${section.emphasis ? ' is-emphasised' : ''}`}
    >
      <h2>{section.title}</h2>

      {section.body?.map((paragraph) => (
        <p key={paragraph.slice(0, 40)}>{withPlaceholders(paragraph)}</p>
      ))}

      {section.list && (
        <>
          {section.list.label && <p>{section.list.label}</p>}
          <ul>
            {section.list.items.map((item) => (
              <li key={item.slice(0, 40)}>{withPlaceholders(item)}</li>
            ))}
          </ul>
        </>
      )}

      {section.table && (
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                {section.table.columns.map((column) => (
                  <th key={column} scope="col">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.table.rows.map((row) => (
                <tr key={row[0]}>
                  {row.map((cell, index) =>
                    index === 0 ? (
                      <th key={cell} scope="row">
                        {withPlaceholders(cell)}
                      </th>
                    ) : (
                      <td key={cell}>{withPlaceholders(cell)}</td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {section.after?.map((paragraph) => (
        <p key={paragraph.slice(0, 40)}>{withPlaceholders(paragraph)}</p>
      ))}
    </section>
  );
}

/**
 * The document itself - used by the public page below and by the pop-up
 * panel in Settings, so both show exactly the same text. `inModal` drops
 * the h1 because the panel's own header already carries the title.
 */
export function LegalDocumentBody({ doc, inModal = false }) {
  return (
    <article className="legal-body">
      {!inModal && <h1>{doc.title}</h1>}
      <p className="legal-meta">
        Version {TERMS_VERSION} · Effective {TERMS_EFFECTIVE_DATE}
      </p>
      <p className="legal-intro">{doc.intro}</p>

      {/* A table of contents, because these are long and people
          arrive looking for one specific answer - most often
          "what do you do with my recordings" or "how do I delete
          my data" - rather than to read from the top. */}
      <nav className="legal-toc" aria-label={`${doc.title} contents`}>
        <ol>
          {doc.sections.map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}`}>{section.title}</a>
            </li>
          ))}
        </ol>
      </nav>

      {doc.sections.map((section) => (
        <LegalSection key={section.id} section={section} />
      ))}
    </article>
  );
}

/** The documents by key, for callers that pick one at runtime. */
export const LEGAL_DOCUMENTS = DOCUMENTS;

export default function LegalPage({ document: documentKey }) {
  const location = useLocation();
  // Falls back to the path so the route can be declared either way -
  // <LegalPage document="terms" /> or just the path /terms.
  const key = documentKey || location.pathname.replace(/^\//, '');
  const doc = DOCUMENTS[key] || PRIVACY_POLICY;
  const others = Object.values(DOCUMENTS).filter((candidate) => candidate.id !== doc.id);

  // A link to a section (e.g. /terms-of-service#payments-cancellation, from the
  // plan-selection notice) scrolls to it once the page has rendered; the browser
  // cannot, because the section does not exist until React draws it.
  useEffect(() => {
    const id = location.hash.replace(/^#/, '');
    if (id) {
      document.getElementById(id)?.scrollIntoView();
    }
  }, [location.hash, doc.id]);

  return (
    <div className="legal-page">
      <div className="legal-shell">
        <header className="legal-topbar">
          <Link to="/" className="legal-brand">
            <Icon name="waveform" size={22} />
            <span>KrishvaQ</span>
          </Link>
          <nav className="legal-topbar-nav" aria-label="Legal documents">
            <Link to="/" className="legal-topbar-link">
              Back to homepage
            </Link>
            {others.map((other) => (
              <Link key={other.id} to={other.path} className="legal-topbar-link">
                {other.title}
              </Link>
            ))}
          </nav>
        </header>

        <LegalDocumentBody doc={doc} />
      </div>
    </div>
  );
}
